import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, forwardRef, input, signal, viewChildren } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const CODE_LENGTH = 6;

/**
 * Six single-digit boxes for OTP / two-factor codes (CoreUI "two-factor" demo).
 * Works as a reactive-forms control whose value is the joined digit string; supports
 * pasting the whole code, backspace to the previous box and arrow-key navigation.
 */
@Component({
  selector: 'app-auth-code-input',
  templateUrl: './auth-code-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AuthCodeInputComponent), multi: true }],
  host: {
    class: 'auth-code',
    role: 'group',
  },
})
export class AuthCodeInputComponent implements ControlValueAccessor {
  readonly label = input<string>('Código de verificação');
  readonly autofocus = input<boolean>(false);
  protected readonly digits = signal<readonly string[]>(Array.from({ length: CODE_LENGTH }, () => ''));
  protected readonly isDisabled = signal<boolean>(false);
  private readonly boxes = viewChildren<ElementRef<HTMLInputElement>>('box');
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor() {
    afterNextRender(() => {
      if (this.autofocus()) {
        this.focusBox(0);
      }
    });
  }

  writeValue(value: string | null): void {
    this.digits.set(this.toDigits(value ?? ''));
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected handleInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const typed: string = this.stripPreviousDigit(target.value.replace(/\D/g, ''), this.digits()[index]);
    if (typed.length > 1) {
      this.fillFrom(index, typed);
      return;
    }
    target.value = typed;
    this.setDigit(index, typed);
    if (typed) {
      this.focusBox(index + 1);
    }
  }

  protected handleKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits()[index] && index > 0) {
      event.preventDefault();
      this.setDigit(index - 1, '');
      this.focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusBox(index + 1);
    }
  }

  protected handlePaste(index: number, event: ClipboardEvent): void {
    const pasted: string = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    event.preventDefault();
    if (pasted) {
      this.fillFrom(index, pasted);
    }
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  protected selectBox(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  private fillFrom(index: number, value: string): void {
    const next: string[] = [...this.digits()];
    const chars: string[] = value.slice(0, CODE_LENGTH - index).split('');
    chars.forEach((char: string, offset: number) => (next[index + offset] = char));
    this.commit(next);
    this.focusBox(Math.min(index + chars.length, CODE_LENGTH - 1));
  }

  private setDigit(index: number, digit: string): void {
    const next: string[] = [...this.digits()];
    next[index] = digit;
    this.commit(next);
  }

  private commit(next: readonly string[]): void {
    this.digits.set(next);
    this.onChange(next.join(''));
  }

  private focusBox(index: number): void {
    this.boxes()[index]?.nativeElement.focus();
  }

  /** Typing over a filled box yields two digits (old + new); keep only the new one. Autofill/paste keep all. */
  private stripPreviousDigit(typed: string, previous: string): string {
    if (typed.length !== 2 || !previous || !typed.includes(previous)) {
      return typed;
    }
    return typed.replace(previous, '');
  }

  private toDigits(value: string): readonly string[] {
    const clean: string = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    return Array.from({ length: CODE_LENGTH }, (unused: unknown, index: number) => clean[index] ?? '');
  }
}
